import { useRef, useState, type ReactNode } from 'react';
import { t } from '../../i18n';
import {
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  Sheet as SheetPrimitive,
  SheetTitle,
} from '../primitives/sheet';
import { ConfirmDialog } from './ConfirmDialog';

type SheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  side?: 'right' | 'left' | 'bottom';
  /** The form inside has unsaved changes: Escape, the overlay and Kapat ask first (admin screens §4.5). */
  dirty?: boolean;
  footer?: ReactNode;
  children: ReactNode;
};

type DrawerProps = Omit<SheetProps, 'side'>;

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  side = 'right',
  dirty = false,
  footer,
  children,
}: SheetProps) {
  const [asking, setAsking] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  // The screen closed the sheet (Back on a ?plan= URL) while the question was open: it goes with the sheet, so the
  // next open shows the form, not the question.
  if (!open && asking) setAsking(false);
  return (
    <SheetPrimitive
      open={open}
      // Escape, the overlay and Kapat all close through here.
      onOpenChange={(next) => {
        if (!next && dirty) setAsking(true);
        else onOpenChange(next);
      }}
    >
      <SheetContent
        side={side}
        data-side={side}
        // A tall bottom sheet scrolls instead of pushing its title and Kapat off the screen.
        className={side === 'bottom' ? 'max-h-full' : undefined}
        // Radix returns focus only to its own Trigger; a sheet is opened by the screen's button.
        onOpenAutoFocus={() => {
          opener.current = document.activeElement as HTMLElement | null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          opener.current?.focus();
        }}
      >
        {side === 'bottom' && (
          <div
            aria-hidden
            className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border-strong"
          />
        )}
        <SheetHeader className="pr-12">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4">{children}</div>
        {footer && <SheetFooter>{footer}</SheetFooter>}
        <ConfirmDialog
          open={asking}
          onOpenChange={setAsking}
          title={t('components.Sheet.discard.title')}
          description={t('components.Sheet.discard.description')}
          confirmLabel={t('components.Sheet.discard.confirm')}
          destructive
          onConfirm={() => {
            setAsking(false);
            onOpenChange(false);
          }}
        />
      </SheetContent>
    </SheetPrimitive>
  );
}

// The phone's action surface: a bottom sheet with a handle (design system §4).
export function Drawer(props: DrawerProps) {
  return <Sheet {...props} side="bottom" />;
}
